import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Clock, AlertTriangle, CheckCircle } from 'lucide-react';

export default function AcessoTemporarioPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('validando'); // validando, valido, expirado, erro
  const [dados, setDados] = useState(null);
  const [tempoRestante, setTempoRestante] = useState(0);

  useEffect(() => {
    validarLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (tempoRestante > 0) {
      const timer = setInterval(() => {
        setTempoRestante(prev => {
          if (prev <= 1) {
            setStatus('expirado');
            localStorage.removeItem('token');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [tempoRestante]);

  const validarLink = async () => {
    try {
      const res = await api.get(`/config/validar-link/${token}`);
      setDados(res.data);
      setTempoRestante(res.data.tempo_restante_segundos);
      setStatus('valido');
      
      // Salvar token temporário
      localStorage.setItem('token', res.data.access_token);
      localStorage.setItem('temp_session', res.data.session_id);
      
      // Salvar dados do usuario temporario para o AuthContext reconhecer
      const tempUser = {
        id: `temp_${token}`,
        name: res.data.nome || 'Visitante',
        email: 'acesso.temporario@sistema.local',
        profile: res.data.perfil,
        status: 'active',
        is_temp: true
      };
      localStorage.setItem('user', JSON.stringify(tempUser));
    } catch (error) {
      if (error.response?.status === 410) {
        setStatus('expirado');
      } else {
        setStatus('erro');
      }
    }
  };

  const entrarNoSistema = () => {
    // Forcar reload para o AuthContext reconhecer o usuario temporario
    window.location.href = '/dashboard';
  };

  const formatTempo = (segundos) => {
    const min = Math.floor(segundos / 60);
    const seg = segundos % 60;
    return `${min}:${seg.toString().padStart(2, '0')}`;
  };

  if (status === 'validando') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Validando link de acesso...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'expirado') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="w-96">
          <CardHeader className="text-center">
            <AlertTriangle size={48} className="mx-auto text-orange-500 mb-2" />
            <CardTitle className="text-orange-600">Link Expirado</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">
              Este link de acesso temporário já expirou. 
              Solicite um novo link ao administrador.
            </p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'erro') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <Card className="w-96">
          <CardHeader className="text-center">
            <AlertTriangle size={48} className="mx-auto text-red-500 mb-2" />
            <CardTitle className="text-red-600">Link Inválido</CardTitle>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-gray-600 mb-4">
              Este link de acesso não é válido. 
              Verifique se copiou corretamente.
            </p>
            <Button onClick={() => navigate('/login')} variant="outline">
              Ir para Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100">
      <Card className="w-[450px]">
        <CardHeader className="text-center bg-green-50 rounded-t-lg">
          <CheckCircle size={48} className="mx-auto text-green-500 mb-2" />
          <CardTitle className="text-green-700">Acesso Autorizado</CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="text-center">
            <p className="text-gray-600 mb-2">Bem-vindo(a),</p>
            <p className="text-xl font-bold text-gray-800">{dados?.nome || 'Visitante'}</p>
          </div>

          <div className="flex justify-center">
            <span className={`px-4 py-2 rounded-full text-sm font-medium ${
              dados?.perfil === 'admin' ? 'bg-red-100 text-red-800' :
              dados?.perfil === 'supervisor' ? 'bg-blue-100 text-blue-800' :
              'bg-green-100 text-green-800'
            }`}>
              Perfil: {dados?.perfil?.toUpperCase()}
            </span>
          </div>

          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-center">
            <div className="flex items-center justify-center gap-2 text-orange-700 mb-1">
              <Clock size={20} />
              <span className="font-medium">Tempo Restante</span>
            </div>
            <p className={`text-3xl font-bold ${tempoRestante < 60 ? 'text-red-600' : 'text-orange-600'}`}>
              {formatTempo(tempoRestante)}
            </p>
            <p className="text-xs text-orange-600 mt-1">
              Após esse tempo, o acesso será encerrado automaticamente
            </p>
          </div>

          <Button onClick={entrarNoSistema} className="w-full bg-blue-600 hover:bg-blue-700 text-lg py-6">
            Entrar no Sistema
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
